import { useEffect, useMemo, useState } from "react";

import {
  addIdRef,
  type DocumentData,
  getDocsFromNamedQuery,
  type GetSnapshotOptions,
  type IDOptions,
  type ModelData,
  type ModelError,
  type QueryModel,
  type QuerySnapshotModel,
  type SnapshotListenOptions,
} from "@doitintl/models-any";
import { type TModelCtor } from "@doitintl/models-types";
import { useQueryClient } from "@tanstack/react-query";

import { useModelsCollection, type UseModelsHookOptions } from "../../react-query";
import { useLoadingValue } from "../util";
import { useIsFirestoreQueryWithKeysEqual } from "./compares";
import { handleRefListenQuery, snapshotToData, useIsFirestoreQueryEqual } from "./helpers";
import {
  type CachingEnabledOptions,
  type CachingOptions,
  type CollectionDataHook,
  type CollectionHook,
  type DataOptions,
  type IDOptionsWithTransform,
  type OnceDataOptions,
  type TReturnData,
  type TypeFromTransform,
  type TypeOrUndefined,
} from "./types";
import { shouldEnabledCaching } from "./utils";

export const useCollection = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: SnapshotListenOptions
): CollectionHook<T> => useCollectionInternal<T>(query, { subscribe: true, ...options });

export const useCollectionOnce = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: GetSnapshotOptions
): CollectionHook<T> => useCollectionInternal<T>(query, options);

export const useCollectionData = <
  T extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel = undefined,
>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: DataOptions<T, IDField, RefField, TTransformModel>
): CollectionDataHook<TReturnData<T, IDField, RefField, TTransformModel>> =>
  useCollectionDataInternal<T, IDField, RefField, TTransformModel>(query, { subscribe: true, ...options });

export const useCollectionDataOnce = <
  T extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel = undefined,
>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: OnceDataOptions<T, IDField, RefField, TTransformModel> & { disableReactQuery?: boolean }
): CollectionDataHook<TReturnData<T, IDField, RefField, TTransformModel>> => {
  const actualSource = options?.source ?? "default";
  return useCollectionDataInternal<T, IDField, RefField, TTransformModel>(query, {
    ...options,
    source: actualSource,
  });
};

// this make sure that if the use has chnaged the query or keys by reference while the content stays the same we get the same query and caching keys
export const useMutableCollectionData = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  cachingKeys?: string[]
) => {
  const [queryState, setQueryState] = useState(query);
  const [cachingKeysState, setCachingState] = useState(cachingKeys);
  useIsFirestoreQueryWithKeysEqual(query ? { query, cachingKeys } : undefined, (value) => {
    setQueryState(value?.query);
    setCachingState(value?.cachingKeys);
  });

  return useMemo(
    () => ({
      query: queryState,
      fullCachingKeys: queryState
        ? [...(cachingKeysState ?? []), "query", queryState.collectionName, ...(queryState.queryKeys ?? [])]
        : undefined,
    }),
    [cachingKeysState, queryState]
  );
};

export const useInvalidateQueryData = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  cachingKeys?: string[]
) => {
  const { fullCachingKeys } = useMutableCollectionData(query, cachingKeys);

  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: fullCachingKeys,
      exact: true,
    });
};

export const useCollectionReactQuery = <
  T extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel = undefined,
>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: UseModelsHookOptions & IDOptionsWithTransform<T, IDField, RefField, TTransformModel> & CachingEnabledOptions
) => {
  const { fullCachingKeys, query: queryState } = useMutableCollectionData(query, options?.cachingKeys);

  const collectionDataResult = useModelsCollection(fullCachingKeys, queryState, options);

  return [
    collectionDataResult.data as QuerySnapshotModel<T>,
    collectionDataResult.isLoading,
    collectionDataResult.error ?? undefined,
  ] as CollectionHook<T>;
};

export const useCollectionDirect = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: UseModelsHookOptions
): CollectionHook<T> => {
  const { error, loading, reset, setError, setValue, value } = useLoadingValue<QuerySnapshotModel<T>, ModelError>();
  const ref = useIsFirestoreQueryEqual<QueryModel<T>>(query, reset);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRefListenQuery({ ref: ref.current, setValue, setError }, options), [ref.current]);

  return useMemo(() => [value as QuerySnapshotModel<T>, loading, error] as CollectionHook<T>, [value, loading, error]);
};

const useCollectionInternal = <T extends DocumentData>(
  query: TypeOrUndefined<QueryModel<T>>,
  options?: UseModelsHookOptions & CachingOptions
): CollectionHook<T> =>
  shouldEnabledCaching(options)
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useCollectionReactQuery(query, options)
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useCollectionDirect(query, options);

const useCollectionDataInternal = <
  T extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel = undefined,
>(
  query: TypeOrUndefined<QueryModel<T>>,
  options: UseModelsHookOptions & IDOptionsWithTransform<T, IDField, RefField, TTransformModel>
): CollectionDataHook<TReturnData<T, IDField, RefField, TTransformModel>> => {
  const { idField, refField, snapshotOptions, transform, ...restOptions } = options;

  const [snapshot, loading, error] = useCollectionInternal<T>(query, restOptions);

  const [values, setValues] = useState<TReturnData<T, IDField, RefField, TTransformModel>[] | undefined>();

  const isLoading = useMemo(() => loading || (values === undefined && !error), [loading, values, error]);

  const { serverTimestamps } = snapshotOptions ?? {};

  useEffect(() => {
    if (!snapshot) {
      setValues(undefined);
      return;
    }

    const promisesOrValues = snapshot.docs.map((doc) =>
      snapshotToData(doc, idField, refField, transform, { serverTimestamps })
    );

    if (!promisesOrValues.length) {
      setValues([]);
      return;
    }

    if (promisesOrValues[0] instanceof Promise) {
      void Promise.all(promisesOrValues).then((results) => {
        setValues(results.filter((r) => !!r) as TReturnData<T, IDField, RefField, TTransformModel>[]);
      });
    } else {
      setValues(promisesOrValues.filter((r) => !!r) as TReturnData<T, IDField, RefField, TTransformModel>[]);
    }
  }, [snapshot, transform, serverTimestamps, idField, refField]);

  return [values, isLoading, error] as CollectionDataHook<TReturnData<T, IDField, RefField, TTransformModel>>;
};

export const useCacheQueryData = <
  T extends DocumentData,
  IDField extends string | undefined,
  RefField extends string | undefined,
>(
  name: string,
  options: IDOptions<IDField, RefField> & { model: TModelCtor<T> }
): CollectionDataHook<ModelData<T, IDField, RefField>> => {
  const [state, setState] = useState<CollectionDataHook<ModelData<T, IDField, RefField>>>([undefined, true, undefined]);

  useEffect(() => {
    getDocsFromNamedQuery<T>(name)
      .then((snapshot) =>
        snapshot.docs.map((doc) => {
          const data = doc.asModelData();
          addIdRef(data, doc, {
            idField: options.idField,
            refField: options.refField,
          });
          return data;
        })
      )
      .then((data) => {
        setState([data, false, undefined] as CollectionDataHook<ModelData<T, IDField, RefField>>);
      })
      .catch((error: unknown) => {
        setState([undefined, false, error as ModelError]);
      });
  }, [name, options.idField, options.model, options.refField]);

  return state;
};
